// ═══════════════════════════════════════════════════════════════════════════════
// Authentication Context
// ═══════════════════════════════════════════════════════════════════════════════

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import {
  api,
  authApi,
  setStoredToken,
  removeStoredToken,
  getStoredToken,
  tryRefreshToken,
  ApiClientError,
} from '../lib/api';
import { clearAppBadge, syncAppBadgeFromServer } from '../lib/appBadge';
import { disableWebPushForCurrentDevice, enableWebPushIfPossible } from '../lib/pushNotification';
import type { UserProfile, LoginResponse, Language, UsageRegion } from '@fdh/shared';

type AuthState = {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
};

type AuthContextValue = AuthState & {
  login: (email: string, password: string, rememberMe?: boolean) => Promise<UserProfile>;
  register: (
    email: string,
    password: string,
    confirmPassword: string,
    nickname: string,
    role: 'employer' | 'helper',
    locale: Language,
    usageRegion: UsageRegion,
    invitationCode?: string
  ) => Promise<UserProfile>;
  loginWithGoogle: (idToken: string) => Promise<UserProfile>;
  registerWithGoogle: (
    idToken: string,
    nickname: string,
    role: 'employer' | 'helper',
    locale: Language,
    usageRegion: UsageRegion,
    invitationCode?: string
  ) => Promise<UserProfile>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<UserProfile | null>;
  updateUser: (user: UserProfile) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

type AuthProviderProps = {
  children: ReactNode;
};

const readCachedUser = (): UserProfile | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const cachedUserRaw = localStorage.getItem('user');
    if (!cachedUserRaw) {
      return null;
    }
    return JSON.parse(cachedUserRaw) as UserProfile;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [state, setState] = useState<AuthState>(() => {
    const cachedUser = readCachedUser();
    return {
      user: cachedUser,
      isAuthenticated: cachedUser != null,
      isLoading: true,
    };
  });
  const foregroundSyncInFlightRef = useRef(false);
  const lastForegroundSyncAtRef = useRef(0);

  // Restore session on mount: prefer /auth/me when an access token exists
  useEffect(() => {
    const initAuth = async () => {
      const cachedUser = readCachedUser();
      const token = getStoredToken();

      if (!token) {
        const refreshed = await tryRefreshToken();
        if (!refreshed) {
          localStorage.removeItem('user');
          setState({ user: null, isAuthenticated: false, isLoading: false });
          return;
        }
      }

      try {
        const response = await api.get<UserProfile>('/auth/me');
        if (response.success && response.data) {
          localStorage.setItem('user', JSON.stringify(response.data));
          setState({ user: response.data, isAuthenticated: true, isLoading: false });
          return;
        }
      } catch {
        // Try one explicit refresh for boot resilience when backend just restarted
      }

      const refreshed = await tryRefreshToken();
      if (refreshed) {
        try {
          const response = await api.get<UserProfile>('/auth/me');
          if (response.success && response.data) {
            localStorage.setItem('user', JSON.stringify(response.data));
            setState({ user: response.data, isAuthenticated: true, isLoading: false });
            return;
          }
        } catch (error) {
          if (error instanceof ApiClientError && error.status === 401) {
            removeStoredToken();
            localStorage.removeItem('user');
            setState({ user: null, isAuthenticated: false, isLoading: false });
            return;
          }
        }
      }

      if (cachedUser) {
        setState({ user: cachedUser, isAuthenticated: true, isLoading: false });
        return;
      }

      removeStoredToken();
      localStorage.removeItem('user');
      setState({ user: null, isAuthenticated: false, isLoading: false });
    };

    initAuth();
  }, []);

  // Proactive refresh (cookie rotation) while logged in — reduces avoidable 401 retries
  useEffect(() => {
    if (!state.isAuthenticated) return undefined;

    void enableWebPushIfPossible();
    void syncAppBadgeFromServer();

    const tick = () => {
      void tryRefreshToken();
    };

    const id = window.setInterval(tick, 5 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [state.isAuthenticated]);

  const login = useCallback(async (email: string, password: string, rememberMe = true) => {
    const response = await api.post<LoginResponse>('/auth/login', {
      email,
      password,
      rememberMe,
    });

    if (!response.success || !response.data) {
      throw new Error(response.error ?? 'Login failed');
    }

    const { accessToken, user: nextUser } = response.data;
    setStoredToken(accessToken);
    localStorage.setItem('user', JSON.stringify(nextUser));
    setState({ user: nextUser, isAuthenticated: true, isLoading: false });
    return nextUser;
  }, []);

  const register = useCallback(async (
    email: string,
    password: string,
    confirmPassword: string,
    nickname: string,
    role: 'employer' | 'helper',
    locale: Language,
    usageRegion: UsageRegion,
    invitationCode?: string
  ): Promise<UserProfile> => {
    const response = await api.post<{ accessToken: string; user: UserProfile }>(
      '/auth/register',
      {
        email,
        password,
        confirmPassword,
        nickname,
        role,
        picsConsentAccepted: true,
        locale,
        usageRegion,
        invitationCode: invitationCode?.trim() ? invitationCode.trim() : undefined,
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error ?? 'Registration failed');
    }

    const { accessToken, user: nextUser } = response.data;
    setStoredToken(accessToken);
    localStorage.setItem('user', JSON.stringify(nextUser));
    setState({ user: nextUser, isAuthenticated: true, isLoading: false });
    return nextUser;
  }, []);

  const loginWithGoogle = useCallback(async (idToken: string): Promise<UserProfile> => {
    const response = await authApi.googleAuth({ idToken, mode: 'login' });
    if (!response.success || !response.data) {
      throw new Error(response.error ?? 'Google login failed');
    }
    const { accessToken, user: nextUser } = response.data;
    setStoredToken(accessToken);
    localStorage.setItem('user', JSON.stringify(nextUser));
    setState({ user: nextUser, isAuthenticated: true, isLoading: false });
    return nextUser;
  }, []);

  const registerWithGoogle = useCallback(async (
    idToken: string,
    nickname: string,
    role: 'employer' | 'helper',
    locale: Language,
    usageRegion: UsageRegion,
    invitationCode?: string
  ): Promise<UserProfile> => {
    const response = await authApi.googleAuth({
      idToken,
      mode: 'register',
      nickname,
      role,
      locale,
      usageRegion,
      invitationCode: invitationCode?.trim() ? invitationCode.trim() : undefined,
    });
    if (!response.success || !response.data) {
      throw new Error(response.error ?? 'Google registration failed');
    }
    const { accessToken, user: nextUser } = response.data;
    setStoredToken(accessToken);
    localStorage.setItem('user', JSON.stringify(nextUser));
    setState({ user: nextUser, isAuthenticated: true, isLoading: false });
    return nextUser;
  }, []);

  const logout = useCallback(async () => {
    await disableWebPushForCurrentDevice();
    await clearAppBadge();

    try {
      await api.post('/auth/logout');
    } catch {
      // Continue logout even if API fails
    }
    removeStoredToken();
    localStorage.removeItem('user');
    setState({ user: null, isAuthenticated: false, isLoading: false });
  }, []);

  const refreshUser = useCallback(async (): Promise<UserProfile | null> => {
    try {
      const response = await api.get<UserProfile>('/auth/me');
      if (response.success && response.data) {
        localStorage.setItem('user', JSON.stringify(response.data));
        setState((prev) => ({ ...prev, user: response.data! }));
        return response.data;
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const updateUser = useCallback((user: UserProfile) => {
    localStorage.setItem('user', JSON.stringify(user));
    setState((prev) => ({ ...prev, user }));
  }, []);

  // PWA foreground sync: when user returns to the app, re-fetch /auth/me so
  // flags like emailVerified are reflected immediately without force-restart.
  useEffect(() => {
    if (!state.isAuthenticated) {
      return undefined;
    }

    const syncOnForeground = () => {
      const now = Date.now();
      const elapsedMs = now - lastForegroundSyncAtRef.current;
      if (elapsedMs < 3000 || foregroundSyncInFlightRef.current) {
        return;
      }
      foregroundSyncInFlightRef.current = true;
      lastForegroundSyncAtRef.current = now;
      void refreshUser().finally(() => {
        foregroundSyncInFlightRef.current = false;
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncOnForeground();
      }
    };

    window.addEventListener('focus', syncOnForeground);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', syncOnForeground);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshUser, state.isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        register,
        loginWithGoogle,
        registerWithGoogle,
        logout,
        refreshUser,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
